import * as Sentry from '@sentry/react';
import { Navigate } from 'react-router-dom';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useBooking } from '@features/booking/state';
import { usePaymentFee } from '@features/booking/hooks/usePaymentFee';
import { StripePaymentForm } from '@features/booking/components/payment/StripePaymentForm';
import { MercadoPagoPaymentForm } from '@features/booking/components/payment/MercadoPagoPaymentForm';
import { markFullStoryEvent, getFullStoryReplayUrl } from '@core/analytics/fullstory';
import type { BookingState } from '@features/booking/state/types';

/**
 * Build a PII-safe snapshot of the booking state for Sentry diagnostics.
 *
 * Captures ONLY the fields needed to diagnose what's missing/corrupted when
 * the route guard fires — diners, dates, slot info, TTL signals. NEVER
 * includes guest name/email/phone, card tokens, encrypted card data, or
 * billing address.
 *
 * Exported for unit tests that pin both inclusions AND exclusions.
 */
export function buildDiagnosticSnapshot(booking: BookingState): Record<string, unknown> {
  const now = Date.now();
  return {
    diners: booking.diners,
    selectedDate: booking.selectedDate,
    selectedTime: booking.selectedTime,
    service: booking.service,
    guaranteeType: booking.guaranteeType,
    feeDeposit: booking.feeDeposit,
    flowType: booking.flowType,
    hasTimeSlots: Array.isArray(booking.timeSlots),
    timeSlotsCount: Array.isArray(booking.timeSlots) ? booking.timeSlots.length : 0,
    startedAt: booking.startedAt,
    ageMs: booking.startedAt ? now - booking.startedAt : null,
    hasCardToken: !!booking.cardToken,
    hasGiftCardToken: !!booking.giftCardToken,
    hasRescheduleId: !!booking.rescheduleId,
    skipConfirmStep: booking.skipConfirmStep,
  };
}

const PaymentPage = () => {
  const restaurant = useRestaurant();
  const booking = useBooking();
  const { isLoading } = booking;
  const { feeBreakdown } = usePaymentFee();

  // BookingProvider hydrates from sessionStorage in an effect (see
  // state/BookingProvider.tsx Effect 1) — on the first paint of a hard
  // refresh / auth-bounce return / 3DS challenge resume, `selectedSlot`
  // is still null and `usePaymentFee` falls into its WL branch,
  // returning `guaranteeType: null`. Without this gate the redirect
  // below would fire before hydration completes and silently boot a
  // valid in-flight deposit user back to `/`. Render nothing until
  // hydration settles; sessionStorage read is synchronous and `useEffect`
  // resolves in the same microtask, so this is a single-frame skip in
  // practice. The form mount cost is also avoided during loading, which
  // is the existing behavior on every booking page.
  if (isLoading) {
    return null;
  }

  // Route-level guard. `/payment` is meaningful only when the user holds a
  // payment-bearing guarantee (deposit | no_show). Without it, the form
  // mounts, collects card details, then hard-fails at submit (the
  // MercadoPagoPaymentForm.tsx:182 / StripePaymentForm sibling guard) —
  // wasting the user's time and creating a conversion leak. Direct URL
  // access can land here three ways: stale sessionStorage from a prior
  // session, browser back-navigation after a slot type change, or
  // deep-link reload after auth bounce. Redirect to the landing page so
  // the booking flow re-derives the correct next step. The form-level
  // submit guard stays as defense-in-depth.
  const requiresPayment =
    feeBreakdown.guaranteeType === 'deposit' || feeBreakdown.guaranteeType === 'no_show';

  if (!requiresPayment) {
    // Capture for telemetry — replaces the visibility we lose by preventing
    // users from reaching the form-level captureException at
    // MercadoPagoPaymentForm.tsx:182.
    //
    // Level: `debug` when the entry path looks like an iOS Safari tab
    // restoration (browser-intrinsic, not actionable) — `document.referrer`
    // is empty AND we're a stale tab the user reopened with no inbound link
    // context. `info` otherwise (in-app navigation, external link from
    // email/social, auth-return bounce, etc. — these ARE actionable
    // signals and should stay visible in the Sentry feed).
    //
    // Why this matters: BF was running ~14-25 events/hr, ~76% of which were
    // browser-intrinsic stale-tab cases that we can't fix. Sentry sees them
    // anyway because the guard fires. Downgrading the unfixable subset to
    // `debug` keeps the dashboard signal high without losing forensics —
    // debug events are still captured, just not in default views or alerts.
    const isLikelyBrowserStaleTab =
      typeof document !== 'undefined' && document.referrer === '';

    // Mark the session in FullStory so it surfaces in the dashboard's
    // event-filter view ("Sessions with event: payment_guard_fired"), and
    // capture the replay URL so the Sentry event below gains a one-click
    // jump to the actual session video. Both calls no-op cleanly when
    // FullStory isn't loaded (dev / preview / blocked). Sentry MEITRE-
    // FRONTEND-V2-BF investigation in 2026-05 stalled at the breadcrumb
    // level because we couldn't see what users actually did before landing
    // here; the replay URL closes that gap.
    markFullStoryEvent('payment_guard_fired', {
      restaurant: restaurant.subdomainPrefix,
      guard_subset: isLikelyBrowserStaleTab ? 'a1_likely_stale_tab' : 'actionable',
      fee_breakdown_guarantee_type: feeBreakdown.guaranteeType ?? '<null>',
      fee_breakdown_total: feeBreakdown.total,
    });
    const fullstoryReplayUrl = getFullStoryReplayUrl();

    Sentry.captureMessage('PaymentPage guard: redirecting non-payment slot', {
      level: isLikelyBrowserStaleTab ? 'debug' : 'info',
      tags: {
        guard: 'payment-route-traditional-slot',
        'restaurant.slug': restaurant.subdomainPrefix,
        guard_subset: isLikelyBrowserStaleTab ? 'a1_likely_stale_tab' : 'actionable',
      },
      extra: {
        feeBreakdownGuaranteeType: feeBreakdown.guaranteeType,
        feeBreakdownTotal: feeBreakdown.total,
        restaurantId: restaurant.id,
        paymentProcessor: restaurant.paymentProcessor,
        // Diagnostic snapshot — see what state the failing user actually has.
        // Tells us if it's a hydration race, ITP wipe, partial restore, etc.
        // PII-safe by construction (see buildDiagnosticSnapshot exclusions).
        diagnosticState: buildDiagnosticSnapshot(booking),
        // Deep-link to the FullStory replay for this session, anchored at
        // the current playhead (the moment the guard fired). Null when
        // FullStory isn't loaded or hasn't established a session yet.
        fullstoryReplayUrl,
      },
    });
    return <Navigate to="/" replace />;
  }

  const isMercadoPago = restaurant.paymentProcessor === 'mercadopago';

  if (isMercadoPago) {
    return <MercadoPagoPaymentForm />;
  }

  if (!restaurant.publicKeyPayment) {
    return (
      <div className="text-center text-red-500 py-8">
        Payment configuration missing. Please contact the restaurant.
      </div>
    );
  }

  return <StripePaymentForm />;
};

export default PaymentPage;
